import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

import { CartLinesUpdateEvent } from '@shopify/events';

document.addEventListener('click', event => {
  if (!(event.target instanceof Element) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest('a[href]');
  if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
  const url = new URL(link.href, location.href);
  const cartUrl = new URL(Theme.routes.cart_url, location.origin);
  if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '')) return;
  const drawer = document.querySelector('#cart-drawer');
  if (typeof drawer?.open !== 'function') return;
  event.preventDefault();
  drawer.open();
}, true);

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  event.promise?.then(({ detail }) => {
    if (!Array.isArray(detail?.items)) return;
    const count = detail.items.reduce((total, item) => total + item.quantity, 0);
    document.querySelectorAll('.nv-count').forEach(badge => { badge.textContent = String(count); });
  }).catch(() => {});
});

document.addEventListener('submit', async event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== 'na-product-form') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (form.dataset.cartPending === 'true') return;
  form.dataset.cartPending = 'true';
  const buttons = [...form.querySelectorAll('[type="submit"]')];
  buttons.forEach(button => { button.disabled = true; });
  let status = form.querySelector('[data-eluna-cart-status]');
  if (!status) {
    status = document.createElement('p');
    status.dataset.elunaCartStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    form.append(status);
  }
  status.textContent = 'Wird hinzugefügt …';
  const mainSelect = form.querySelector('#na-variant-select');
  const freeSelect = form.querySelector('#na-free-select');
  const freeOption = [...mainSelect.options].find(option => option.textContent.trim() === freeSelect.selectedOptions[0].textContent.trim());
  if (!freeOption) {
    status.textContent = 'Bitte wähle eine verfügbare Gratisfarbe.';
    form.dataset.cartPending = 'false';
    buttons.forEach(button => { button.disabled = false; });
    return;
  }
  const items = [{id:mainSelect.value,quantity:1},{id:freeOption.value,quantity:1}];
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(component => component.dataset.sectionId))];
  if (!sections.includes('cart-drawer-section')) sections.push('cart-drawer-section');
  const payload = {items,sections:sections.slice(0,5),sections_url:location.pathname};
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(item => ({merchandiseId:item.id,quantity:item.quantity})),promise:deferred.promise}));
  try {
    const response = await fetch(Theme.routes.cart_add_url, {method:'POST',headers:{Accept:'application/json','Content-Type':'application/json'},body:JSON.stringify(payload)});
    const added = await response.json();
    if (!response.ok || added.status) throw new Error(added.description || added.message || 'Der Artikel konnte nicht hinzugefügt werden.');
    const cartResponse = await fetch(Theme.routes.cart_url + '.js', {headers:{Accept:'application/json'}});
    if (!cartResponse.ok) throw new Error('Der Artikel wurde hinzugefügt. Öffne den Warenkorb erneut, um ihn anzuzeigen.');
    const cart = await cartResponse.json();
    deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'product-form-component',sourceId:form.getAttribute('id'),itemCount:2,sections:added.sections,didError:false}});
    status.textContent = ''; 
    requestAnimationFrame(() => document.querySelector('#cart-drawer')?.open());
  } catch (error) {
    deferred.reject(error);
    status.textContent = error.message || 'Bitte versuche es erneut.';
  } finally {
    form.dataset.cartPending = 'false';
    buttons.forEach(button => { button.disabled = false; });
  }
}, true);
